import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';
import { colors } from '~/styles';
import CardMenu from './CardMenu';

const Styled = {
  Card: styled.div`
    position: relative;
    background: white;
    border-radius: 0.25rem;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    display: flex;
    flex-direction: column;
    gap: 0.375rem;

    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: grab;
    user-select: none;

    ${({ isDraggable }) =>
      !isDraggable &&
      css`
        cursor: default;
      `}

    ${({ isDragging }) =>
      isDragging &&
      css`
        outline: 1px solid ${colors.primary};
      `}

    transition: box-shadow 0.2s ease-in-out;

    ${CardMenu.Trigger} {
      visibility: hidden;
    }

    &:hover,
    &:focus,
    &:active,
    &:focus-within {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

      ${CardMenu.Trigger} {
        visibility: visible;
      }
    }
  `,

  Header: styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    user-select: none;
    text-decoration: none;
    padding: 0.25rem 0;

    .icon {
      font-size: 0.75rem;
    }

    &:empty {
      display: none;
    }
  `,

  Title: styled.div`
    display: block;
    line-height: 1.25rem;
    padding-right: 1.5rem;
    width: fit-content;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  `,

  DropIndicator: styled.div`
    position: absolute;
    z-index: 10;
    background-color: ${colors.primary};
    pointer-events: none;
    box-sizing: border-box;
    height: 2px;
    left: 0;
    right: 0;
    border-radius: 1rem;
    display: flex;
    align-items: center;

    &::before {
      content: '';
      width: 0.625rem;
      height: 0.625rem;
      border-radius: 50%;
      background-color: ${colors.primary};
      margin-left: -7px;
    }

    ${({ edge }) => {
      switch (edge) {
        case 'top':
          return css`
            top: -5px;
          `;
        case 'bottom':
          return css`
            bottom: -5px;
          `;
      }
    }}
  `,
};

export default function Card({ id, listId, children, isDraggable = true, renderDragPreview, ...props }) {
  const ref = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null); // NEW
  const [dragState, setDragState] = useState({ type: 'idle' });

  useEffect(() => {
    if (!isDraggable) {
      return;
    }

    return combine(
      draggable({
        element: ref.current,
        getInitialData: () => ({ type: 'card', id, listId }),
        onGenerateDragPreview({ nativeSetDragImage }) {
          if (!renderDragPreview) return;

          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: pointerOutsideOfPreview({ x: 8, y: 8 }),
            render({ container }) {
              setDragState({ type: 'preview', container });
              return () => setDragState({ type: 'dragging' });
            },
          });
        },
        onDragStart: () => setIsDragging(true),
        onDrop: () => {
          setIsDragging(false);
          setDragState({ type: 'idle' });
        },
      }),

      dropTargetForElements({
        element: ref.current,
        getData: ({ input, element }) => {
          return attachClosestEdge(
            { type: 'card', id, listId },
            {
              input,
              element,
              allowedEdges: ['top', 'bottom'],
            },
          );
        },
        getIsSticky: () => true,
        onDragEnter: (args) => {
          if (args.source.data.id !== id) {
            setClosestEdge(extractClosestEdge(args.self.data));
          }
        },
        onDrag: (args) => {
          if (args.source.data.id !== id) {
            setClosestEdge(extractClosestEdge(args.self.data));
          }
        },
        onDragLeave: () => {
          setClosestEdge(null);
        },
        onDrop: () => {
          setClosestEdge(null);
        },
      }),
    );
  }, [id, listId, isDraggable, renderDragPreview]);

  return (
    <Styled.Card ref={ref} isDragging={isDragging} isDraggable={isDraggable} {...props}>
      {children}
      {closestEdge && <Styled.DropIndicator edge={closestEdge} />}

      {dragState.type === 'preview' &&
        typeof renderDragPreview === 'function' &&
        createPortal(renderDragPreview({ id, listId, ...props }), dragState.container)}
    </Styled.Card>
  );
}

Card.Header = Styled.Header;
Card.Title = Styled.Title;
